import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  ComboBox as AriaComboBox,
  Input,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Label,
  Text,
  type ComboBoxProps as AriaComboBoxProps,
} from 'react-aria-components';
import { ChevronDown } from 'lucide-react';
import { useInterfacePortalContainer } from '@/foundation/ui/InterfacePortalContext';
import { cn } from '@/foundation/utils/cn';

interface ComboBoxItem {
  id: string;
  label: string;
}

export type ComboBoxSize = 'sm' | 'md' | 'lg';

export interface ComboBoxProps<T extends ComboBoxItem> extends Omit<
  AriaComboBoxProps<T>,
  'children'
> {
  label?: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  items: T[];
  className?: string;
  inputClassName?: string;
  size?: ComboBoxSize;
  children?: (item: T) => ReactNode;
}

const sizeConfig: Record<
  ComboBoxSize,
  {
    height: number | string;
    fontSize: number | string;
    labelSize: number | string;
    paddingInline: number;
    radius: number | string;
  }
> = {
  sm: {
    height: 'var(--control-height-sm)',
    fontSize: 'var(--text-xs)',
    labelSize: 'var(--text-xs)',
    paddingInline: 14,
    radius: 'var(--control-radius-sm)',
  },
  md: {
    height: 'var(--control-height-md)',
    fontSize: 'var(--text-sm)',
    labelSize: 'var(--text-xs)',
    paddingInline: 16,
    radius: 'var(--control-radius-md)',
  },
  lg: {
    height: 'var(--control-height-lg)',
    fontSize: 'var(--text-base)',
    labelSize: 'var(--text-sm)',
    paddingInline: 18,
    radius: 'var(--control-radius-lg)',
  },
};

function getInputWrapperStyle(
  config: (typeof sizeConfig)[ComboBoxSize],
  isFocused: boolean,
  isInvalid: boolean,
  isDisabled: boolean
): CSSProperties {
  const borderColor = isInvalid
    ? 'var(--color-error)'
    : isFocused
      ? 'var(--color-accent)'
      : 'var(--color-surface-border)';

  const ringColor = isInvalid ? 'var(--color-error-subtle)' : 'var(--color-accent-subtle)';

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: config.height,
    padding: `0 ${config.paddingInline}px`,
    borderRadius: config.radius,
    background: 'var(--color-surface-solid)',
    border: `1px solid ${borderColor}`,
    boxShadow: isFocused
      ? `0 0 0 3px ${ringColor}, inset 0 0.5px 0 var(--color-surface-highlight)`
      : 'var(--control-shadow-inset)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    opacity: isDisabled ? 0.5 : 1,
    cursor: isDisabled ? 'not-allowed' : undefined,
    fontFamily: 'var(--font-sans)',
  };
}

export function ComboBox<T extends ComboBoxItem>({
  label,
  description,
  errorMessage,
  placeholder = 'Search...',
  items,
  className,
  inputClassName,
  size = 'md',
  children,
  ...props
}: ComboBoxProps<T>) {
  const [focused, setFocused] = useState(false);
  const config = sizeConfig[size];
  const portalContainer = useInterfacePortalContainer();

  return (
    <AriaComboBox className={cn('flex flex-col gap-1', className)} {...props}>
      {label && (
        <Label
          style={{
            display: 'block',
            fontSize: config.labelSize,
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-secondary)',
            marginBottom: 6,
            fontFamily: 'var(--font-sans)',
          }}
        >
          {label}
        </Label>
      )}

      <div style={{ position: 'relative' }}>
        <div
          style={getInputWrapperStyle(
            config,
            focused,
            Boolean(errorMessage),
            Boolean(props.isDisabled)
          )}
        >
          <Input
            className={inputClassName}
            placeholder={placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              color: 'var(--color-text)',
              letterSpacing: '-0.01em',
              fontSize: config.fontSize,
              height: '100%',
            }}
          />
          <Button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: 9999,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background 0.15s',
              padding: 0,
            }}
          >
            <ChevronDown size={15} strokeWidth={2} style={{ color: 'var(--color-text-muted)' }} />
          </Button>
        </div>
      </div>

      {description && !errorMessage && (
        <Text
          slot="description"
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 'var(--weight-normal)',
            color: 'var(--color-text-secondary)',
            marginTop: 5,
            fontFamily: 'var(--font-sans)',
          }}
        >
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text
          slot="errorMessage"
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 'var(--weight-medium)',
            color: 'var(--color-error-text)',
            marginTop: 5,
            fontFamily: 'var(--font-sans)',
          }}
        >
          {errorMessage}
        </Text>
      )}

      <Popover
        UNSTABLE_portalContainer={portalContainer ?? undefined}
        placement="bottom start"
        offset={8}
        className={cn(
          'w-[var(--trigger-width)] max-h-60 overflow-auto rounded-xl p-1 outline-none',
          'border border-[var(--color-surface-border)] bg-[var(--color-surface-solid)] shadow-[var(--shadow-md)]'
        )}
        style={{
          background: 'var(--color-surface-solid)',
          borderColor: 'var(--color-surface-border)',
          boxShadow: 'var(--shadow-md)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <ListBox className="outline-none" items={items}>
          {children ??
            ((item) => (
              <ListBoxItem
                key={item.id}
                id={item.id}
                className={cn(
                  'cursor-pointer rounded-lg px-3 py-2.5 outline-none',
                  'text-[var(--text-sm)] text-[var(--color-text-secondary)]',
                  'hover:bg-[var(--color-surface-muted)] focus:bg-[var(--color-surface-muted)]',
                  'data-[selected]:bg-[var(--color-accent-subtle)] data-[selected]:text-[var(--color-accent-text)]'
                )}
                style={{
                  lineHeight: 1.35,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                {item.label}
              </ListBoxItem>
            ))}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}
